import React, { useState, useEffect } from 'react';
import { auth, db, analytics } from './firebase';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import { doc, getDoc, collection, query, orderBy, limit, getDocs } from 'firebase/firestore';
import { logEvent } from 'firebase/analytics';
import Login from './components/Login';
import BabyProfile from './components/BabyProfile';
import AddRecord from './components/AddRecord';
import RecordList from './components/RecordList';
import GrowthChart from './components/GrowthChart';
import AverageComparison from './components/AverageComparison';
import EditProfile from './components/EditProfile';
import LoadingSpinner from './components/LoadingSpinner';
import ShareCard from './components/ShareCard';
import EditRecord from './components/EditRecord';
import CalendarView from './components/CalendarView';
import DevelopmentalInfo from './components/DevelopmentalInfo';
import VaccinationSchedule from './components/VaccinationSchedule';
import GrowthSpeed from './components/GrowthSpeed';
import BadgeCollection from './components/BadgeCollection';
import './App.css';

// 일러스트 이미지 import
const babyImages = {
  '0-3_common': '/images/baby_0-3_common.png',
  '4-6_common': '/images/baby_4-6_common.png',
  '7-9_boy': '/images/baby_7-9_boy.png',
  '7-9_girl': '/images/baby_7-9_girl.png',
  '10-12_boy': '/images/baby_10-12_boy.png',
  '10-12_girl': '/images/baby_10-12_girl.png',
  '13-18_boy': '/images/baby_13-18_boy.png',
  '13-18_girl': '/images/baby_13-18_girl.png',
};

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showLogin, setShowLogin] = useState(false);
  const [profile, setProfile] = useState(null);
  const [profileLoading, setProfileLoading] = useState(false);
  const [showAddRecord, setShowAddRecord] = useState(false);
  const [showRecordList, setShowRecordList] = useState(false);
  const [showGrowthChart, setShowGrowthChart] = useState(false);
  const [showAverageComparison, setShowAverageComparison] = useState(false);
  const [showEditProfile, setShowEditProfile] = useState(false);
  const [showShareCard, setShowShareCard] = useState(false);
  const [latestRecord, setLatestRecord] = useState(null);
  const [previousRecord, setPreviousRecord] = useState(null);
  const [showSuccessToast, setShowSuccessToast] = useState(false);
  const [showEditRecord, setShowEditRecord] = useState(false);
  const [showCalendarView, setShowCalendarView] = useState(false);
  const [showDevelopmentalInfo, setShowDevelopmentalInfo] = useState(false);
  const [showVaccinationSchedule, setShowVaccinationSchedule] = useState(false);
  const [showGrowthSpeed, setShowGrowthSpeed] = useState(false);
  const [showBadgeCollection, setShowBadgeCollection] = useState(false);
  const [guestMode, setGuestMode] = useState(false);
  const [showGuestWarning, setShowGuestWarning] = useState(false);

  useEffect(() => {
    // Analytics: 페이지 방문
    if (analytics) {
      logEvent(analytics, 'page_view', { page_title: 'Bebe Note - Home' });
    }

    // 인스타그램 인앱 브라우저 등에서 Firebase Auth가 응답하지 않을 경우를 대비한 timeout
    const timeoutId = setTimeout(() => {
      setLoading(false);
    }, 3000); // 3초 후 강제로 로딩 종료

    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      clearTimeout(timeoutId); // Auth 응답이 오면 timeout 취소
      setUser(currentUser);

      if (currentUser) {
        // 프로필 로드
        await loadProfile(currentUser.uid);
      } else {
        setProfile(null);
      }

      setLoading(false);
    });

    return () => {
      unsubscribe();
      clearTimeout(timeoutId);
    };
  }, []);

  const loadProfile = async (userId) => {
    setProfileLoading(true);
    try {
      const profileDoc = await getDoc(doc(db, 'users', userId, 'profile', 'info'));
      if (profileDoc.exists()) {
        setProfile(profileDoc.data());
        // 프로필 로드 후 최신 기록도 함께 로드
        await loadLatestRecord(userId);
      } else {
        setProfile(null);
      }
    } catch (error) {
      console.error('프로필 로드 실패:', error);
      setProfile(null);
    } finally {
      setProfileLoading(false);
    }
  };

  const loadLatestRecord = async (userId) => {
    try {
      const recordsRef = collection(db, 'users', userId, 'records');
      const q = query(recordsRef, orderBy('date', 'desc'), limit(2));
      const querySnapshot = await getDocs(q);

      if (!querySnapshot.empty) {
        setLatestRecord({ id: querySnapshot.docs[0].id, ...querySnapshot.docs[0].data() });
        // 두 번째 기록이 있으면 저장
        if (querySnapshot.docs.length > 1) {
          setPreviousRecord({ id: querySnapshot.docs[1].id, ...querySnapshot.docs[1].data() });
        } else {
          setPreviousRecord(null);
        }
      } else {
        setLatestRecord(null);
        setPreviousRecord(null);
      }
    } catch (error) {
      console.error('최신 기록 로드 실패:', error);
      setLatestRecord(null);
      setPreviousRecord(null);
    }
  };

  const handleRecordSuccess = async () => {
    if (user) {
      await loadLatestRecord(user.uid);
      // 축하 토스트 표시
      setShowSuccessToast(true);
      setTimeout(() => {
        setShowSuccessToast(false);
      }, 2500);
    } else if (guestMode) {
      // 비회원 모드: localStorage에서 기록 불러오기
      loadGuestRecords();
    }
  };

  // 비회원 기록 불러오기
  const loadGuestRecords = () => {
    try {
      const records = JSON.parse(localStorage.getItem('guestRecords') || '[]');
      if (records.length > 0) {
        // 날짜 기준으로 정렬 (최신순)
        records.sort((a, b) => new Date(b.date) - new Date(a.date));
        setLatestRecord(records[0]);
        if (records.length > 1) {
          setPreviousRecord(records[1]);
        } else {
          setPreviousRecord(null);
        }
      } else {
        setLatestRecord(null);
        setPreviousRecord(null);
      }
    } catch (error) {
      console.error('비회원 기록 로드 실패:', error);
      setLatestRecord(null);
      setPreviousRecord(null);
    }
  };

  const handleLogout = async () => {
    try {
      await signOut(auth);
    } catch (error) {
      console.error('로그아웃 실패:', error);
      alert('로그아웃에 실패했습니다.');
    }
  };

  const handleAuthClick = () => {
    if (user) {
      handleLogout();
    } else {
      setShowLogin(true);
    }
  };

  const handleProfileComplete = async () => {
    if (user) {
      await loadProfile(user.uid);
    }
  };

  const startGuestMode = () => {
    setShowGuestWarning(true);
  };

  const confirmGuestMode = () => {
    setGuestMode(true);
    setShowGuestWarning(false);

    // 생후 8개월로 가정한 가상 프로필 설정
    const birthDate = new Date();
    birthDate.setMonth(birthDate.getMonth() - 8);
    const birthDateString = birthDate.toISOString().split('T')[0];

    setProfile({
      name: '우리아기',
      birthDate: birthDateString,
      gender: 'boy'
    });

    // 비회원 체험 모드는 항상 깨끗한 샘플 데이터로 시작
    // 기존 localStorage 데이터는 무시하고 새로운 샘플 데이터 생성
    const today = new Date();
    const oneMonthAgo = new Date();
    oneMonthAgo.setMonth(today.getMonth() - 1);
    const twoMonthsAgo = new Date();
    twoMonthsAgo.setMonth(today.getMonth() - 2);
    const threeMonthsAgo = new Date();
    threeMonthsAgo.setMonth(today.getMonth() - 3);

    // 샘플 성장 기록 데이터 (4개)
    const sampleRecords = [
      {
        id: 'sample-1',
        date: threeMonthsAgo.toISOString().split('T')[0],
        height: 65.5,
        weight: 7.2,
        memo: '건강검진 시 측정'
      },
      {
        id: 'sample-2',
        date: twoMonthsAgo.toISOString().split('T')[0],
        height: 67.2,
        weight: 7.8,
        memo: '많이 자라는 중'
      },
      {
        id: 'sample-3',
        date: oneMonthAgo.toISOString().split('T')[0],
        height: 68.8,
        weight: 8.3,
        memo: '잘 크고 있어요'
      },
      {
        id: 'sample-4',
        date: today.toISOString().split('T')[0],
        height: 70.5,
        weight: 8.9,
        memo: '최근 측정 기록'
      }
    ];

    // localStorage에 샘플 데이터 저장
    localStorage.setItem('guestRecords', JSON.stringify(sampleRecords));

    // 비회원 기록 불러오기
    loadGuestRecords();

    // Analytics: 게스트 모드 시작
    if (analytics) {
      logEvent(analytics, 'guest_mode_started');
    }
  };

  // 더 이상 사용하지 않음 - 비회원도 모든 기능 사용 가능

  const handleLoginSuccess = () => {
    setGuestMode(false);

    // Analytics: 로그인 성공
    if (analytics) {
      logEvent(analytics, 'login', { method: 'google_or_apple' });
    }
  };

  // Analytics 헬퍼 함수들
  const trackFeatureUse = (featureName) => {
    if (analytics) {
      logEvent(analytics, 'feature_used', { feature_name: featureName });
    }
  };

  if (loading || profileLoading) {
    return <LoadingSpinner fullScreen message="불러오는 중..." />;
  }

  // 로그인하지 않은 경우 (비회원 모드도 아닌 경우)
  if (!user && !guestMode) {
    return (
      <div className="App">

        <header style={{
          backgroundColor: '#FAF3E8',
          padding: '16px 20px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: '12px'
        }}>
          <div style={{
            fontSize: '18px',
            fontWeight: 'bold',
            color: '#E8A87C',
            whiteSpace: 'nowrap',
            flex: '0 0 auto',
            fontFamily: 'Pacifico, cursive'
          }}>
            🍼 bébé note
          </div>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            marginLeft: 'auto'
          }}>
            <button
              onClick={() => window.location.href = 'https://dodo-bebe.site'}
              style={{
                backgroundColor: '#D4956B',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                padding: '8px 16px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: 'bold',
                display: 'flex',
                alignItems: 'center',
                gap: '6px'
              }}
            >
              🏠 아뜰리에로
            </button>
            <button
              onClick={() => setShowLogin(true)}
              style={{
                backgroundColor: '#E8A87C',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                padding: '8px 16px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: 'bold'
              }}
            >
              로그인
            </button>
          </div>
        </header>

        <main style={{
          padding: '20px',
          maxWidth: '500px',
          margin: '0 auto',
          backgroundColor: '#F5E6D3',
          minHeight: 'calc(100vh - 60px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          <div style={{
            textAlign: 'center',
            color: '#8B7355'
          }}>
            <img
              src="/images/baby_0-3_common.png"
              alt="baby"
              onClick={startGuestMode}
              style={{
                width: '200px',
                height: '200px',
                objectFit: 'contain',
                display: 'block',
                margin: '0 auto 24px',
                borderRadius: '24px',
                cursor: 'pointer',
                transition: 'transform 0.2s'
              }}
              onMouseOver={(e) => e.target.style.transform = 'scale(1.05)'}
              onMouseOut={(e) => e.target.style.transform = 'scale(1)'}
            />
            <h2 style={{color: '#E8A87C', marginBottom: '12px', fontSize: '28px', fontFamily: 'Pacifico, cursive'}}>bébé note</h2>
            <p style={{fontSize: '16px'}}>우리 아기의 소중한 성장을 기록해보세요</p>
            <p style={{fontSize: '14px', color: '#D4956B', marginTop: '12px'}}>
              👆 아기 그림을 눌러 체험해보세요
            </p>
          </div>
        </main>

        {showLogin && <Login onClose={() => setShowLogin(false)} onLoginSuccess={handleLoginSuccess} />}

        {/* 비회원 모드 경고 팝업 */}
        {showGuestWarning && (
          <div className="fade-in" style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 1000,
            padding: '20px'
          }}>
            <div className="slide-up" style={{
              backgroundColor: 'white',
              borderRadius: '24px',
              padding: '40px',
              maxWidth: '400px',
              width: '100%',
              textAlign: 'center'
            }}>
              <div style={{ fontSize: '48px', marginBottom: '16px' }}>⚠️</div>
              <h3 style={{ color: '#E8A87C', marginBottom: '16px', fontSize: '22px' }}>
                체험하기
              </h3>
              <p style={{ color: '#8B7355', fontSize: '15px', lineHeight: '1.6', marginBottom: '24px' }}>
                비회원 모드로 앱을 체험할 수 있습니다.<br />
                <strong style={{ color: '#E57373' }}>기록은 저장되지 않으며</strong>,<br />
                페이지를 새로고침하면 사라집니다.
              </p>
              <p style={{ color: '#8B7355', fontSize: '14px', marginBottom: '24px', backgroundColor: '#FFF8F0', padding: '12px', borderRadius: '12px' }}>
                💡 <strong>로그인</strong>하시면 기록이 안전하게 저장됩니다!
              </p>
              <div style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: '12px'
              }}>
                <button
                  onClick={() => setShowGuestWarning(false)}
                  style={{
                    padding: '14px',
                    backgroundColor: 'white',
                    border: '2px solid #F5E6D3',
                    borderRadius: '12px',
                    fontSize: '15px',
                    fontWeight: 'bold',
                    color: '#8B7355',
                    cursor: 'pointer'
                  }}
                >
                  취소
                </button>
                <button
                  onClick={confirmGuestMode}
                  style={{
                    padding: '14px',
                    backgroundColor: '#E8A87C',
                    border: 'none',
                    borderRadius: '12px',
                    fontSize: '15px',
                    fontWeight: 'bold',
                    color: 'white',
                    cursor: 'pointer'
                  }}
                >
                  체험하기
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // 로그인했지만 프로필이 없는 경우 (비회원 모드가 아닌 경우)
  if (!profile && !guestMode) {
    return <BabyProfile userId={user.uid} onComplete={handleProfileComplete} />;
  }

  // 나이 계산 함수 (개월 수)
  const calculateAge = (birthDate) => {
    const birth = new Date(birthDate);
    const today = new Date();
    const months = (today.getFullYear() - birth.getFullYear()) * 12 + (today.getMonth() - birth.getMonth());
    return months;
  };

  // 받침 확인 함수
  const hasFinalConsonant = (name) => {
    if (!name || name.length === 0) return false;
    const lastChar = name.charAt(name.length - 1);
    const code = lastChar.charCodeAt(0);
    // 한글 유니코드 범위: 0xAC00 ~ 0xD7A3
    if (code >= 0xAC00 && code <= 0xD7A3) {
      return (code - 0xAC00) % 28 !== 0;
    }
    return false;
  };

  // 조사 선택 함수 (은/는)
  const getParticle = (name, withConsonant, withoutConsonant) => {
    return hasFinalConsonant(name) ? withConsonant : withoutConsonant;
  };

  // 나이 표시 텍스트 생성
  const getAgeText = (months) => {
    const years = Math.floor(months / 12);
    const remainingMonths = months % 12;

    // 연령대별 이모티콘
    const getAgeEmoji = (months) => {
      if (months < 3) return '👶';
      if (months < 6) return '🍼';
      if (months < 12) return '🧸';
      if (months < 18) return '🎈';
      if (months < 30) return '🎨';
      if (months < 42) return '🎭';
      if (months < 54) return '🎯';
      if (months < 66) return '🎓';
      return '🎒';
    };

    const emoji = getAgeEmoji(months);

    if (years === 0) {
      return `만 ${remainingMonths}개월이에요 ${emoji}`;
    } else if (remainingMonths === 0) {
      return `만 ${years}세에요 ${emoji}`;
    } else {
      return `만 ${years}세에요 ${emoji}`;
    }
  };

  // 나이에 맞는 아기 이미지 선택
  const getBabyImage = (months, gender) => {
    if (months <= 3) return babyImages['0-3_common'];
    if (months <= 6) return babyImages['4-6_common'];
    if (months <= 9) return babyImages[`7-9_${gender}`];
    if (months <= 12) return babyImages[`10-12_${gender}`];
    return babyImages[`13-18_${gender}`]; // 13개월 이상은 모두 13-18 이미지 사용
  };

  // 태어난 지 며칠 지났는지 계산
  const calculateDaysSinceBirth = (birthDate) => {
    const birth = new Date(birthDate);
    const today = new Date();
    const diffTime = Math.abs(today - birth);
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  // 마일스톤 체크
  const getMilestone = (birthDate) => {
    const days = calculateDaysSinceBirth(birthDate);
    const milestones = [
      { days: 100, label: '백일', emoji: '🎂' },
      { days: 200, label: '200일', emoji: '🎉' },
      { days: 300, label: '300일', emoji: '🎊' },
      { days: 365, label: '돌', emoji: '🎁' },
      { days: 500, label: '500일', emoji: '🎈' },
      { days: 730, label: '2돌', emoji: '🎁' },
      { days: 1000, label: '1000일', emoji: '✨' },
      { days: 1095, label: '3돌', emoji: '🎁' }
    ];

    // 정확히 마일스톤 날짜이거나 ±3일 이내
    for (const milestone of milestones) {
      if (Math.abs(days - milestone.days) <= 3) {
        return milestone;
      }
    }
    return null;
  };

  const babyAge = calculateAge(profile.birthDate);
  const milestone = getMilestone(profile.birthDate);

  // 측정 리마인더 체크 (마지막 측정 후 30일 경과 여부)
  const needsReminder = () => {
    if (!latestRecord) return true; // 기록이 없으면 항상 표시
    const lastRecordDate = new Date(latestRecord.date);
    const today = new Date();
    const daysSinceLastRecord = Math.floor((today - lastRecordDate) / (1000 * 60 * 60 * 24));
    return daysSinceLastRecord >= 30;
  };

  return (
    <div className="App fade-in">
      <header style={{
        backgroundColor: '#FAF3E8',
        padding: '16px 20px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: '12px'
      }}>
        <div style={{
          fontSize: '18px',
          fontWeight: 'bold',
          color: '#E8A87C',
          whiteSpace: 'nowrap',
          flex: '0 0 auto',
          fontFamily: 'Pacifico, cursive'
        }}>
          🍼 bébé note
        </div>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          marginLeft: 'auto'
        }}>
          {user && profile && (
            <button
              onClick={() => setShowEditProfile(true)}
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                fontSize: '20px',
                padding: '4px',
                display: 'flex',
                alignItems: 'center',
                color: '#8B7355'
              }}
              title="프로필 수정"
            >
              ⚙️
            </button>
          )}
          <button
            onClick={() => window.location.href = 'https://dodo-bebe.site'}
            style={{
              backgroundColor: '#D4956B',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              padding: '8px 16px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: 'bold',
              display: 'flex',
              alignItems: 'center',
              gap: '6px'
            }}
          >
            🏠 아뜰리에로
          </button>
          {guestMode ? (
            <button
              onClick={() => setShowLogin(true)}
              style={{
                backgroundColor: '#E8A87C',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                padding: '8px 16px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: 'bold'
              }}
            >
              로그인
            </button>
          ) : (
            <button
              onClick={handleAuthClick}
              style={{
                backgroundColor: '#E8A87C',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                padding: '8px 16px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: 'bold'
              }}
            >
              {user ? '로그아웃' : '로그인'}
            </button>
          )}
        </div>
      </header>

      {/* 비회원 모드 경고 배너 */}
      {guestMode && (
        <div style={{
          backgroundColor: '#FFF3CD',
          borderBottom: '2px solid #FFD700',
          padding: '12px 20px',
          textAlign: 'center',
          color: '#856404',
          fontSize: '14px',
          fontWeight: 'bold'
        }}>
          ⚠️ 비회원 모드: 기록이 저장되지 않습니다
          <button
            onClick={() => setShowLogin(true)}
            style={{
              marginLeft: '12px',
              backgroundColor: '#E8A87C',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              padding: '6px 12px',
              fontSize: '13px',
              fontWeight: 'bold',
              cursor: 'pointer'
            }}
          >
            로그인하기
          </button>
        </div>
      )}

      <main style={{
        padding: '20px',
        maxWidth: '500px',
        margin: '0 auto',
        backgroundColor: '#F5E6D3',
        minHeight: '100vh'
      }}>
        <div style={{
          backgroundColor: '#FFFBF5',
          borderRadius: '32px',
          padding: '40px',
          textAlign: 'center',
          marginBottom: '20px',
          boxShadow: '0 4px 16px rgba(139, 115, 85, 0.12)'
        }}>
          <img
            src={getBabyImage(babyAge, profile.gender)}
            alt="baby"
            style={{
              width: '200px',
              height: '200px',
              objectFit: 'contain',
              display: 'block',
              margin: '0 auto 16px',
              borderRadius: '24px'
            }}
          />
          <h2 style={{
            color: '#E8A87C',
            fontSize: '24px',
            marginBottom: '12px'
          }}>
            {profile.name}
          </h2>
          {milestone && (
            <div style={{
              display: 'inline-block',
              backgroundColor: '#FFE5B4',
              color: '#E8A87C',
              padding: '6px 16px',
              borderRadius: '20px',
              fontSize: '13px',
              fontWeight: 'bold',
              marginBottom: '12px',
              border: '2px solid #E8A87C'
            }}>
              <span style={{ marginRight: '6px' }}>{milestone.emoji}</span>
              {milestone.label} 축하해요!
            </div>
          )}
          <div style={{color: '#8B7355', fontSize: '14px', marginBottom: '16px'}}>
            {profile.birthDate} 출생
          </div>
          {latestRecord ? (
            <>
              <div style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: '12px',
                marginTop: '16px',
                marginBottom: '16px'
              }}>
                {/* 키 위젯 */}
                <div style={{
                  backgroundColor: '#FAF3E8',
                  borderRadius: '16px',
                  padding: '16px',
                  textAlign: 'center',
                  border: '2px solid #E8A87C'
                }}>
                  <div style={{ fontSize: '28px', marginBottom: '8px' }}>📏</div>
                  <div style={{
                    color: '#E8A87C',
                    fontSize: '20px',
                    fontWeight: 'bold',
                    marginBottom: '4px'
                  }}>
                    {latestRecord.height}
                  </div>
                  <div style={{ color: '#8B7355', fontSize: '11px', marginBottom: '8px' }}>cm</div>
                  {previousRecord && latestRecord.height !== previousRecord.height && (
                    <div style={{
                      fontSize: '11px',
                      color: latestRecord.height > previousRecord.height ? '#A8D5BA' : '#8B7355',
                      fontWeight: 'bold'
                    }}>
                      {latestRecord.height > previousRecord.height ? '↗️' : '↘️'}
                      {Math.abs(latestRecord.height - previousRecord.height).toFixed(1)}cm
                    </div>
                  )}
                </div>

                {/* 몸무게 위젯 */}
                <div style={{
                  backgroundColor: '#FAF3E8',
                  borderRadius: '16px',
                  padding: '16px',
                  textAlign: 'center',
                  border: '2px solid #D4956B'
                }}>
                  <div style={{ fontSize: '28px', marginBottom: '8px' }}>⚖️</div>
                  <div style={{
                    color: '#D4956B',
                    fontSize: '20px',
                    fontWeight: 'bold',
                    marginBottom: '4px'
                  }}>
                    {latestRecord.weight}
                  </div>
                  <div style={{ color: '#8B7355', fontSize: '11px', marginBottom: '8px' }}>kg</div>
                  {previousRecord && latestRecord.weight !== previousRecord.weight && (
                    <div style={{
                      fontSize: '11px',
                      color: latestRecord.weight > previousRecord.weight ? '#A8D5BA' : '#8B7355',
                      fontWeight: 'bold'
                    }}>
                      {latestRecord.weight > previousRecord.weight ? '↗️' : '↘️'}
                      {Math.abs(latestRecord.weight - previousRecord.weight).toFixed(1)}kg
                    </div>
                  )}
                </div>
              </div>

              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '12px',
                marginTop: '12px'
              }}>
                <div style={{color: '#8B7355', fontSize: '12px'}}>
                  측정일: {latestRecord.date}
                </div>
                <button
                  onClick={() => guestMode ? setShowLogin(true) : setShowEditRecord(true)}
                  style={{
                    backgroundColor: '#FFFBF5',
                    border: '2px solid #E8A87C',
                    borderRadius: '8px',
                    padding: '4px 12px',
                    cursor: 'pointer',
                    fontSize: '11px',
                    color: '#E8A87C',
                    fontWeight: 'bold',
                    transition: 'all 0.2s'
                  }}
                  onMouseOver={(e) => {
                    e.target.style.backgroundColor = '#FAF3E8';
                  }}
                  onMouseOut={(e) => {
                    e.target.style.backgroundColor = '#FFFBF5';
                  }}
                >
                  {guestMode ? '🔒 로그인' : '✏️ 수정'}
                </button>
              </div>
            </>
          ) : (
            <div style={{
              padding: '20px',
              backgroundColor: '#FAF3E8',
              borderRadius: '16px',
              marginTop: '16px'
            }}>
              <div style={{fontSize: '32px', marginBottom: '12px'}}>📝</div>
              <p style={{color: '#E8A87C', fontWeight: 'bold', marginBottom: '8px'}}>
                아직 기록이 없어요
              </p>
              <p style={{color: '#8B7355', fontSize: '13px', marginBottom: '16px'}}>
                {profile.name}의 첫 성장 기록을 추가해보세요!
              </p>
              <button
                onClick={() => setShowAddRecord(true)}
                style={{
                  backgroundColor: '#E8A87C',
                  color: 'white',
                  border: 'none',
                  borderRadius: '12px',
                  padding: '12px 24px',
                  fontSize: '14px',
                  fontWeight: 'bold',
                  cursor: 'pointer'
                }}
              >
                첫 기록 추가하기
              </button>
            </div>
          )}
        </div>

        <div style={{
          textAlign: 'center',
          color: '#8B7355',
          fontSize: '18px',
          fontWeight: 'bold',
          marginBottom: '20px'
        }}>
          우리 {profile.name}{getParticle(profile.name, '이는', '는')} {getAgeText(babyAge)}
        </div>

        {/* 월별 성장 통계 */}
        {latestRecord && previousRecord && (
          <div style={{
            backgroundColor: '#E8F5E9',
            borderRadius: '16px',
            padding: '20px',
            marginBottom: '20px'
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginBottom: '12px',
              gap: '8px'
            }}>
              <span style={{ fontSize: '20px' }}>📊</span>
              <h3 style={{
                color: '#66BB6A',
                fontSize: '16px',
                margin: 0
              }}>
                최근 성장 통계
              </h3>
            </div>
            <div style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: '12px'
            }}>
              <div style={{
                backgroundColor: '#FFFBF5',
                borderRadius: '12px',
                padding: '12px',
                textAlign: 'center'
              }}>
                <div style={{ fontSize: '18px', marginBottom: '4px' }}>📏</div>
                <div style={{
                  fontSize: '18px',
                  fontWeight: 'bold',
                  color: '#66BB6A',
                  marginBottom: '4px'
                }}>
                  +{(latestRecord.height - previousRecord.height).toFixed(1)}cm
                </div>
                <div style={{ fontSize: '11px', color: '#8B7355' }}>
                  키 변화
                </div>
              </div>
              <div style={{
                backgroundColor: '#FFFBF5',
                borderRadius: '12px',
                padding: '12px',
                textAlign: 'center'
              }}>
                <div style={{ fontSize: '18px', marginBottom: '4px' }}>⚖️</div>
                <div style={{
                  fontSize: '18px',
                  fontWeight: 'bold',
                  color: '#66BB6A',
                  marginBottom: '4px'
                }}>
                  +{(latestRecord.weight - previousRecord.weight).toFixed(1)}kg
                </div>
                <div style={{ fontSize: '11px', color: '#8B7355' }}>
                  몸무게 변화
                </div>
              </div>
            </div>
            <div style={{
              textAlign: 'center',
              marginTop: '12px',
              fontSize: '11px',
              color: '#8B7355'
            }}>
              {previousRecord.date} → {latestRecord.date}
            </div>
          </div>
        )}

        {/* 측정 리마인더 */}
        {latestRecord && needsReminder() && (
          <div style={{
            backgroundColor: '#FFF8E1',
            border: '2px solid #FFD54F',
            borderRadius: '16px',
            padding: '16px',
            marginBottom: '20px',
            textAlign: 'center'
          }}>
            <div style={{ fontSize: '24px', marginBottom: '8px' }}>⏰</div>
            <p style={{
              color: '#F57C00',
              fontSize: '14px',
              fontWeight: 'bold',
              marginBottom: '8px'
            }}>
              측정한 지 30일이 지났어요!
            </p>
            <p style={{
              color: '#8B7355',
              fontSize: '12px',
              marginBottom: '12px'
            }}>
              {profile.name}의 성장을 기록해주세요
            </p>
            <button
              onClick={() => setShowAddRecord(true)}
              style={{
                backgroundColor: '#FFB74D',
                color: 'white',
                border: 'none',
                borderRadius: '10px',
                padding: '10px 20px',
                fontSize: '13px',
                fontWeight: 'bold',
                cursor: 'pointer'
              }}
            >
              지금 측정하기
            </button>
          </div>
        )}

        {/* 공유 버튼 */}
        <button
          onClick={() => setShowShareCard(true)}
          style={{
            width: '100%',
            backgroundColor: '#E8A87C',
            color: 'white',
            border: 'none',
            borderRadius: '16px',
            padding: '16px',
            fontSize: '15px',
            fontWeight: 'bold',
            cursor: 'pointer',
            marginBottom: '20px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px'
          }}
        >
          📤 성장 기록 공유하기
        </button>

        <div style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: '16px'
        }}>
          <button
            onClick={() => {
              trackFeatureUse('growth_chart');
              setShowGrowthChart(true);
            }}
            style={{
              backgroundColor: '#FFFBF5',
              border: '1px solid #F5E6D3',
              borderRadius: '16px',
              padding: '24px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: 'bold',
              color: '#D4956B',
              transition: 'all 0.2s'
            }}
          >
            📊 성장 그래프
          </button>
          <button
            onClick={() => {
              trackFeatureUse('add_record');
              setShowAddRecord(true);
            }}
            style={{
              backgroundColor: '#FFFBF5',
              border: '1px solid #F5E6D3',
              borderRadius: '16px',
              padding: '24px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: 'bold',
              color: '#D4956B',
              transition: 'all 0.2s'
            }}
          >
            📝 기록 추가
          </button>
          <button
            onClick={() => {
              trackFeatureUse('average_comparison');
              setShowAverageComparison(true);
            }}
            style={{
              backgroundColor: '#FFFBF5',
              border: '1px solid #F5E6D3',
              borderRadius: '16px',
              padding: '24px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: 'bold',
              color: '#D4956B',
              transition: 'all 0.2s'
            }}
          >
            📈 평균 비교
          </button>
          <button
            onClick={() => {
              trackFeatureUse('record_list');
              setShowRecordList(true);
            }}
            style={{
              backgroundColor: '#FFFBF5',
              border: '1px solid #F5E6D3',
              borderRadius: '16px',
              padding: '24px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: 'bold',
              color: '#D4956B',
              transition: 'all 0.2s'
            }}
          >
            📋 전체 기록
          </button>
          <button
            onClick={() => {
              trackFeatureUse('calendar_view');
              setShowCalendarView(true);
            }}
            style={{
              backgroundColor: '#FFFBF5',
              border: '1px solid #F5E6D3',
              borderRadius: '16px',
              padding: '24px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: 'bold',
              color: '#D4956B',
              transition: 'all 0.2s'
            }}
          >
            📅 캘린더
          </button>
          <button
            onClick={() => {
              trackFeatureUse('developmental_info');
              setShowDevelopmentalInfo(true);
            }}
            style={{
              backgroundColor: '#FFFBF5',
              border: '1px solid #F5E6D3',
              borderRadius: '16px',
              padding: '24px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: 'bold',
              color: '#D4956B',
              transition: 'all 0.2s'
            }}
          >
            👶 발달 정보
          </button>
          <button
            onClick={() => setShowVaccinationSchedule(true)}
            style={{
              backgroundColor: '#FFFBF5',
              border: '1px solid #F5E6D3',
              borderRadius: '16px',
              padding: '24px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: 'bold',
              color: '#D4956B',
              transition: 'all 0.2s'
            }}
          >
            💉 예방접종
          </button>
          <button
            onClick={() => {
              trackFeatureUse('growth_speed');
              setShowGrowthSpeed(true);
            }}
            style={{
              backgroundColor: '#FFFBF5',
              border: '1px solid #F5E6D3',
              borderRadius: '16px',
              padding: '24px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: 'bold',
              color: '#D4956B',
              transition: 'all 0.2s'
            }}
          >
            🚀 성장 속도
          </button>
          <button
            onClick={() => {
              trackFeatureUse('badge_collection');
              setShowBadgeCollection(true);
            }}
            style={{
              backgroundColor: '#FFFBF5',
              border: '1px solid #F5E6D3',
              borderRadius: '16px',
              padding: '24px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: 'bold',
              color: '#D4956B',
              transition: 'all 0.2s'
            }}
          >
            🏆 성장 뱃지
          </button>
        </div>

        {/* 비회원 로그인 유도 배너 */}
        {guestMode && (
          <div style={{
            backgroundColor: '#FFFBF5',
            border: '2px solid #E8A87C',
            borderRadius: '20px',
            padding: '24px',
            marginTop: '24px',
            textAlign: 'center',
            boxShadow: '0 4px 12px rgba(232, 168, 124, 0.15)'
          }}>
            <div style={{ fontSize: '32px', marginBottom: '12px' }}>💝</div>
            <h3 style={{
              color: '#E8A87C',
              fontSize: '18px',
              fontWeight: 'bold',
              marginBottom: '12px'
            }}>
              우리 아기의 소중한 순간을 안전하게 저장하세요
            </h3>
            <p style={{
              color: '#8B7355',
              fontSize: '14px',
              lineHeight: '1.6',
              marginBottom: '16px'
            }}>
              로그인하시면 아이의 성장 기록이 안전하게 저장되고<br />
              언제 어디서나 확인할 수 있어요
            </p>
            <button
              onClick={() => setShowLogin(true)}
              style={{
                backgroundColor: '#E8A87C',
                color: 'white',
                border: 'none',
                borderRadius: '12px',
                padding: '14px 32px',
                fontSize: '15px',
                fontWeight: 'bold',
                cursor: 'pointer',
                boxShadow: '0 2px 8px rgba(232, 168, 124, 0.3)',
                transition: 'all 0.2s'
              }}
              onMouseOver={(e) => {
                e.target.style.transform = 'scale(1.05)';
                e.target.style.boxShadow = '0 4px 12px rgba(232, 168, 124, 0.4)';
              }}
              onMouseOut={(e) => {
                e.target.style.transform = 'scale(1)';
                e.target.style.boxShadow = '0 2px 8px rgba(232, 168, 124, 0.3)';
              }}
            >
              로그인하고 기록 저장하기
            </button>
          </div>
        )}
      </main>

      {showLogin && <Login onClose={() => setShowLogin(false)} onLoginSuccess={handleLoginSuccess} />}
      {showAddRecord && (
        <AddRecord
          userId={user ? user.uid : 'guest'}
          onClose={() => setShowAddRecord(false)}
          onSuccess={handleRecordSuccess}
          guestMode={guestMode}
        />
      )}
      {showRecordList && (
        <RecordList
          userId={user ? user.uid : 'guest'}
          onClose={() => setShowRecordList(false)}
          onUpdate={handleRecordSuccess}
          guestMode={guestMode}
        />
      )}
      {showGrowthChart && (
        <GrowthChart
          userId={user ? user.uid : 'guest'}
          profile={profile}
          onClose={() => setShowGrowthChart(false)}
          guestMode={guestMode}
        />
      )}
      {showAverageComparison && (
        <AverageComparison
          profile={profile}
          latestRecord={latestRecord}
          babyAge={babyAge}
          onClose={() => setShowAverageComparison(false)}
        />
      )}
      {showEditProfile && (
        <EditProfile
          userId={user.uid}
          profile={profile}
          onClose={() => setShowEditProfile(false)}
          onUpdate={handleProfileComplete}
        />
      )}
      {showShareCard && (
        <ShareCard
          profile={profile}
          latestRecord={latestRecord}
          babyAge={babyAge}
          onClose={() => setShowShareCard(false)}
        />
      )}
      {showEditRecord && latestRecord && (
        <EditRecord
          userId={user.uid}
          record={latestRecord}
          onClose={() => setShowEditRecord(false)}
          onSuccess={handleRecordSuccess}
        />
      )}
      {showCalendarView && (
        <CalendarView
          userId={user ? user.uid : 'guest'}
          onClose={() => setShowCalendarView(false)}
          guestMode={guestMode}
        />
      )}
      {showDevelopmentalInfo && (
        <DevelopmentalInfo
          profile={profile}
          onClose={() => setShowDevelopmentalInfo(false)}
        />
      )}
      {showVaccinationSchedule && (
        <VaccinationSchedule
          profile={profile}
          onClose={() => setShowVaccinationSchedule(false)}
        />
      )}
      {showGrowthSpeed && (
        <GrowthSpeed
          userId={user ? user.uid : 'guest'}
          profile={profile}
          onClose={() => setShowGrowthSpeed(false)}
          guestMode={guestMode}
        />
      )}
      {showBadgeCollection && (
        <BadgeCollection
          userId={user ? user.uid : 'guest'}
          profile={profile}
          onClose={() => setShowBadgeCollection(false)}
          guestMode={guestMode}
        />
      )}

      {/* 성공 토스트 */}
      {showSuccessToast && (
        <div className="toast-enter" style={{
          position: 'fixed',
          bottom: '80px',
          left: '50%',
          transform: 'translateX(-50%)',
          backgroundColor: '#A8D5BA',
          color: 'white',
          padding: '16px 32px',
          borderRadius: '16px',
          boxShadow: '0 4px 16px rgba(0, 0, 0, 0.2)',
          zIndex: 2000,
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          fontSize: '15px',
          fontWeight: 'bold'
        }}>
          <span style={{ fontSize: '24px' }}>🎉</span>
          <span>기록이 추가되었어요!</span>
        </div>
      )}
    </div>
  );
}

export default App;